import { Icon, Typography } from "@saviynt/design-system";
import { FormattedMessage, useIntl } from "react-intl";
import { useAuth } from "../../contexts/AuthContext";
import { ScrollHint } from "../OnboardingParts";

/** Derive a friendly first name from a login/email (e.g. "rahul.kumar@x.com" → "Rahul"). */
function deriveFirstName(username?: string | null): string | null {
  if (!username) return null;
  const token = username.split("@")[0].split(/[._-]/)[0];
  if (!token) return null;
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

interface Day0WelcomeSectionProps {
  /** Ref callback so the parent can scroll-snap to this section. */
  sectionRef: (el: HTMLElement | null) => void;
  /** Jump to the get-started steps. */
  onScrollToSteps: () => void;
  /** Jump to the video overview screen (next section). */
  onScrollToVideo: () => void;
  scrollHintLabel: string;
}

/** Screen 1 — welcome hero with headline, lede, and CTAs. */
export function Day0WelcomeSection({
  sectionRef,
  onScrollToSteps,
  onScrollToVideo,
  scrollHintLabel,
}: Day0WelcomeSectionProps) {
  const intl = useIntl();
  const { userEmail } = useAuth();
  const firstName = deriveFirstName(userEmail);

  const zumaAccent = {
    z: (chunks: React.ReactNode) => (
      <span className="Onboarding__zuma">{chunks}</span>
    ),
  };

  return (
    <section
      ref={sectionRef}
      className="relative flex h-full snap-start snap-always flex-col items-center justify-center overflow-hidden px-6 text-center"
    >
      <div className="relative z-[1] flex flex-col items-center gap-7">
        {/* Eyebrow pill */}
        <span className="Onboarding__eyebrow Onboarding__reveal Onboarding__reveal--1">
          <Typography
            kind="label"
            color="--color-foreground-green-subtle"
            className="uppercase !tracking-[0.14em]"
          >
            <FormattedMessage
              id="T0Ywv7"
              description="Eyebrow tag on the day-1 onboarding screen"
              defaultMessage="DISCOVER • PROTECT • MANAGE"
            />
          </Typography>
        </span>

        {/* Headline */}
        <Typography
          kind="display1-bold"
          color="--color-foreground-neutral-bold"
          className="Onboarding__reveal Onboarding__reveal--2 !text-[2.75rem] !leading-[1.05] tracking-tight sm:!text-[3.75rem] lg:!text-[4.5rem]"
        >
          {firstName ? (
            <FormattedMessage
              id="xQh9p7"
              description="Personalized onboarding heading with the user's first name"
              defaultMessage="Welcome to <z>Zuma</z>, {name}"
              values={{ ...zumaAccent, name: firstName }}
            />
          ) : (
            <FormattedMessage
              id="7aQh8C"
              description="Main heading on the day-1 onboarding screen"
              defaultMessage="Welcome to <z>Zuma</z>"
              values={zumaAccent}
            />
          )}
        </Typography>

        {/* Lede */}
        <div className="Onboarding__reveal Onboarding__reveal--3 max-w-[46rem]">
          <Typography
            kind="body1"
            color="--color-foreground-neutral-medium"
            className="!text-[1.0625rem] !font-medium !leading-[1.6] sm:!text-[1.25rem]"
          >
            <FormattedMessage
              id="1uFjzj"
              description="Onboarding hero subheading describing the product"
              defaultMessage="You're moments away from exploring the Enterprise AI Identity Security Platform. Zuma will help you discover every AI agent and non-human identity in your environment, assess their risk and help with remediation, manage their full lifecycle, and control access in real time. Let's get started."
            />
          </Typography>
        </div>

        {/* CTAs */}
        <div className="Onboarding__reveal Onboarding__reveal--4 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            className="Onboarding__btn Onboarding__btn--primary"
            onClick={onScrollToSteps}
          >
            {intl.formatMessage({
              id: "d0whVg",
              defaultMessage: "Let's get you started",
              description:
                "Primary onboarding CTA that jumps to the get-started steps",
            })}
          </button>
          <button
            type="button"
            className="Onboarding__btn Onboarding__btn--ghost"
            onClick={onScrollToVideo}
          >
            <Icon
              kind="PlayFilled"
              size="xSmall"
              color="--color-foreground-green-subtle"
            />
            {intl.formatMessage({
              id: "slFqZs",
              defaultMessage: "Watch Zuma in Action",
              description:
                "Secondary CTA on the welcome screen that opens the video tour",
            })}
          </button>
        </div>
      </div>

      <ScrollHint label={scrollHintLabel} onClick={onScrollToVideo} />
    </section>
  );
}
